// Affix catalogue. ADR 0017 §J / docs/04 §4.5.
//
// Static `AffixDefinition` master pool (72 affixes = 35 prefix + 37 suffix).
// The rolled `Affix` instance picks a `tier` and a value within the
// template's `valuesByTier` range. Roll weight / drop pools are a separate
// ADR scope (ADR 0017 §未確定事項).
//
// ID schema (ADR 0017 §J #1):
//   affix.<kind>.<family>.<key>     e.g. `affix.prefix.damage.fire`
//
// Tier scaling: each affix pins a Tier-1 and Tier-5 center value; tiers
// 2-4 are interpolated on a quasi-exponential curve (late-tier growth).
// Each tier carries a ±10% roll range (rounded to integers; low tiers may
// collapse to min === max, which is allowed). Numbers are provisional and
// recalibrated in Phase 4 (ADR 0017 §未確定事項).
import { CREATURE_TYPES, ELEMENTS, } from '@renavora/shared';
/** Normalized tier curve (T1 = 0.0 .. T5 = 1.0), quasi-exponential. */
const TIER_CURVE = [0, 0.18, 0.4, 0.66, 1.0];
/**
 * Build a 5-tier roll table from a Tier-1 and Tier-5 center value.
 * Each tier gets a ±10% range, rounded to integers.
 */
function tiers(t1, t5) {
    const out = {};
    for (let i = 0; i < 5; i++) {
        const center = t1 + (t5 - t1) * TIER_CURVE[i];
        const spread = center * 0.1;
        out[(i + 1)] = {
            min: Math.round(center - spread),
            max: Math.round(center + spread),
        };
    }
    return out;
}
/** Build a 5-tier table of fixed integer values (no roll range). */
function fixedTiers(vals) {
    return {
        1: { min: vals[0], max: vals[0] },
        2: { min: vals[1], max: vals[1] },
        3: { min: vals[2], max: vals[2] },
        4: { min: vals[3], max: vals[3] },
        5: { min: vals[4], max: vals[4] },
    };
}
// Slot groupings reused across affix specs.
const WEAPON_ACCESSORY = ['weaponMain', 'amulet', 'ring1', 'ring2'];
const WEAPON_HANDS = ['weaponMain', 'hands'];
const ARMOR_SLOTS = ['head', 'chest', 'hands', 'boots', 'belt', 'cloak'];
const ARMOR_OFFHAND_ACCESSORY = [
    'head',
    'chest',
    'hands',
    'boots',
    'belt',
    'cloak',
    'offHand',
    'amulet',
    'ring1',
    'ring2',
];
/** Debuff archetypes matching races.ts RaceStatusResistance 7 axes. */
const PROC_DEBUFFS = ['bleed', 'burn', 'freeze', 'paralyze', 'petrify', 'daze', 'poison'];
/** The 6 non-physical elements (physical resistance is covered by armor). */
const RESIST_ELEMENTS = ELEMENTS.filter((el) => el !== 'physical');
// =============================================================================
// Prefix affixes (35) — offensive. ADR 0017 §1.
//
// family damage(7) / penetration(7) / offense(6) / proc(9) / conditional(2)
// / projectile(4).
//
// typeAffinity is not stored on AffixDefinition — drop-pool wiring against
// ItemBase.typeAffinity is a separate ADR (ADR 0017 §未確定事項).
// =============================================================================
// family `damage` — element damage % (7), percent-mul
const DAMAGE_AFFIXES = ELEMENTS.map((el) => ({
    id: `affix.prefix.damage.${el}`,
    kind: 'prefix',
    family: 'damage',
    applicableSlots: WEAPON_ACCESSORY,
    valuesByTier: tiers(8, 40),
    valueKind: 'percent-mul',
}));
// family `penetration` — element resist penetration % (7), percent-add
const PENETRATION_AFFIXES = ELEMENTS.map((el) => ({
    id: `affix.prefix.penetration.${el}`,
    kind: 'prefix',
    family: 'penetration',
    applicableSlots: WEAPON_ACCESSORY,
    valuesByTier: tiers(4, 20),
    valueKind: 'percent-add',
}));
// family `offense` — generic offensive stats (6)
const OFFENSE_AFFIXES = [
    {
        id: 'affix.prefix.offense.attack_speed',
        kind: 'prefix',
        family: 'offense',
        applicableSlots: ['weaponMain', 'hands', 'ring1', 'ring2'],
        valuesByTier: tiers(4, 20),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.prefix.offense.crit_chance',
        kind: 'prefix',
        family: 'offense',
        applicableSlots: ['weaponMain', 'head', 'hands', 'amulet', 'ring1', 'ring2'],
        valuesByTier: tiers(2, 10),
        valueKind: 'percent-add',
    },
    {
        id: 'affix.prefix.offense.crit_damage',
        kind: 'prefix',
        family: 'offense',
        applicableSlots: ['weaponMain', 'head', 'amulet', 'ring1', 'ring2'],
        valuesByTier: tiers(12, 60),
        valueKind: 'percent-add',
    },
    {
        id: 'affix.prefix.offense.accuracy',
        kind: 'prefix',
        family: 'offense',
        applicableSlots: ['weaponMain', 'head', 'hands', 'ring1', 'ring2'],
        valuesByTier: tiers(3, 15),
        valueKind: 'percent-add',
    },
    {
        id: 'affix.prefix.offense.cooldown_rate',
        kind: 'prefix',
        family: 'offense',
        applicableSlots: ['hands', 'amulet', 'cloak'],
        valuesByTier: tiers(3, 15),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.prefix.offense.dot_damage',
        kind: 'prefix',
        family: 'offense',
        applicableSlots: ['weaponMain', 'hands', 'amulet'],
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
];
// family `proc` — status infliction (9): 7 debuff chances + duration + potency
const PROC_CHANCE_AFFIXES = PROC_DEBUFFS.map((debuff) => {
    // freeze / paralyze / petrify are hard CC — lower proc chance.
    const hardCC = debuff === 'freeze' || debuff === 'paralyze' || debuff === 'petrify';
    return {
        id: `affix.prefix.proc.${debuff}_chance`,
        kind: 'prefix',
        family: 'proc',
        applicableSlots: WEAPON_HANDS,
        valuesByTier: hardCC ? tiers(2, 10) : tiers(3, 15),
        valueKind: 'percent-add',
    };
});
const PROC_MODIFIER_AFFIXES = [
    {
        id: 'affix.prefix.proc.ailment_duration',
        kind: 'prefix',
        family: 'proc',
        applicableSlots: WEAPON_ACCESSORY,
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.prefix.proc.ailment_potency',
        kind: 'prefix',
        family: 'proc',
        applicableSlots: ['weaponMain', 'amulet'],
        valuesByTier: tiers(6, 30),
        valueKind: 'percent-mul',
    },
];
// family `conditional` — conditional damage (2)
const CONDITIONAL_AFFIXES = [
    {
        id: 'affix.prefix.conditional.execute_damage',
        kind: 'prefix',
        family: 'conditional',
        applicableSlots: WEAPON_ACCESSORY,
        valuesByTier: tiers(6, 30),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.prefix.conditional.vs_marked',
        kind: 'prefix',
        family: 'conditional',
        applicableSlots: ['weaponMain', 'amulet'],
        valuesByTier: tiers(5, 25),
        valueKind: 'percent-mul',
    },
];
// family `projectile` — projectile / range (4)
//
// projectile.extra / .pierce / .speed depend on a sim projectile mechanic
// that is not yet implemented (Codex integration-review ISSUE B); catalogue
// definitions are populated but sim application is Phase 4. projectile.range
// applies via the existing Skill.range field.
const PROJECTILE_AFFIXES = [
    {
        id: 'affix.prefix.projectile.extra',
        kind: 'prefix',
        family: 'projectile',
        applicableSlots: ['weaponMain', 'offHand'],
        applicableBaseKinds: ['bow', 'quiver'],
        valuesByTier: fixedTiers([1, 1, 1, 2, 2]),
        valueKind: 'integer',
    },
    {
        id: 'affix.prefix.projectile.pierce',
        kind: 'prefix',
        family: 'projectile',
        applicableSlots: ['weaponMain', 'offHand'],
        applicableBaseKinds: ['bow', 'quiver'],
        valuesByTier: fixedTiers([1, 1, 2, 2, 3]),
        valueKind: 'integer',
    },
    {
        id: 'affix.prefix.projectile.speed',
        kind: 'prefix',
        family: 'projectile',
        applicableSlots: ['weaponMain', 'offHand'],
        applicableBaseKinds: ['bow', 'quiver'],
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.prefix.projectile.range',
        kind: 'prefix',
        family: 'projectile',
        applicableSlots: ['weaponMain', 'amulet'],
        valuesByTier: tiers(5, 25),
        valueKind: 'percent-mul',
    },
];
export const PREFIX_AFFIXES = [
    ...DAMAGE_AFFIXES,
    ...PENETRATION_AFFIXES,
    ...OFFENSE_AFFIXES,
    ...PROC_CHANCE_AFFIXES,
    ...PROC_MODIFIER_AFFIXES,
    ...CONDITIONAL_AFFIXES,
    ...PROJECTILE_AFFIXES,
];
// =============================================================================
// Suffix affixes (37) — defensive / utility. ADR 0017 §2.
//
// family defense(10) / status(8) / utility(3) / sustain(2) / slayer(8)
// / minion(3) / placeable(2) / support(1).
// =============================================================================
// family `defense` — armor + element resist + shield (10)
const DEFENSE_AFFIXES = [
    {
        id: 'affix.suffix.defense.max_hp',
        kind: 'suffix',
        family: 'defense',
        applicableSlots: [...ARMOR_SLOTS, 'offHand', 'amulet', 'ring1', 'ring2'],
        valuesByTier: tiers(40, 240),
        valueKind: 'flat',
    },
    {
        id: 'affix.suffix.defense.armor_pct',
        kind: 'suffix',
        family: 'defense',
        applicableSlots: [...ARMOR_SLOTS, 'offHand'],
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.suffix.defense.all_resist',
        kind: 'suffix',
        family: 'defense',
        applicableSlots: ['amulet', 'ring1', 'ring2', 'cloak', 'offHand'],
        valuesByTier: tiers(3, 15),
        valueKind: 'percent-add',
    },
    ...RESIST_ELEMENTS.map((el) => ({
        id: `affix.suffix.defense.resist_${el}`,
        kind: 'suffix',
        family: 'defense',
        applicableSlots: ARMOR_OFFHAND_ACCESSORY,
        valuesByTier: tiers(6, 30),
        valueKind: 'percent-add',
    })),
    {
        id: 'affix.suffix.defense.shield_damage_reduction',
        kind: 'suffix',
        family: 'defense',
        applicableSlots: ['offHand', 'chest', 'head'],
        applicableBaseKinds: ['shield'],
        valuesByTier: tiers(4, 20),
        valueKind: 'percent-mul',
    },
];
// family `status` — status-effect resistance (8): general + 7 debuff-specific
const STATUS_AFFIXES = [
    {
        id: 'affix.suffix.status.resist_all',
        kind: 'suffix',
        family: 'status',
        applicableSlots: ['head', 'chest', 'belt', 'amulet', 'ring1', 'ring2'],
        valuesByTier: tiers(4, 20),
        valueKind: 'percent-add',
    },
    ...PROC_DEBUFFS.map((debuff) => ({
        id: `affix.suffix.status.resist_${debuff}`,
        kind: 'suffix',
        family: 'status',
        applicableSlots: ARMOR_OFFHAND_ACCESSORY,
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-add',
    })),
];
// family `utility` — mobility / defensive utility (3)
const UTILITY_AFFIXES = [
    {
        id: 'affix.suffix.utility.move_speed',
        kind: 'suffix',
        family: 'utility',
        applicableSlots: ['boots', 'belt', 'cloak'],
        valuesByTier: tiers(3, 15),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.suffix.utility.dodge',
        kind: 'suffix',
        family: 'utility',
        applicableSlots: ['boots', 'cloak', 'ring1', 'ring2'],
        valuesByTier: tiers(2, 10),
        valueKind: 'percent-add',
    },
    {
        id: 'affix.suffix.utility.block_chance',
        kind: 'suffix',
        family: 'utility',
        applicableSlots: ['offHand', 'chest', 'head'],
        applicableBaseKinds: ['shield'],
        valuesByTier: tiers(4, 20),
        valueKind: 'percent-add',
    },
];
// family `sustain` — healing / regen (2)
const SUSTAIN_AFFIXES = [
    {
        id: 'affix.suffix.sustain.life_on_hit',
        kind: 'suffix',
        family: 'sustain',
        applicableSlots: ['weaponMain', 'hands', 'amulet', 'ring1', 'ring2'],
        valuesByTier: tiers(4, 24),
        valueKind: 'flat',
    },
    {
        id: 'affix.suffix.sustain.hp_regen',
        kind: 'suffix',
        family: 'sustain',
        applicableSlots: ['chest', 'belt', 'amulet', 'ring1', 'ring2'],
        valuesByTier: tiers(3, 18),
        valueKind: 'flat',
    },
];
// family `slayer` — creature-type damage (8), matching races.ts CREATURE_TYPES
const SLAYER_AFFIXES = CREATURE_TYPES.map((creature) => ({
    id: `affix.suffix.slayer.${creature}`,
    kind: 'suffix',
    family: 'slayer',
    applicableSlots: WEAPON_ACCESSORY,
    valuesByTier: tiers(6, 30),
    valueKind: 'percent-mul',
}));
// family `minion` — summoned-creature buffs (3)
const MINION_AFFIXES = [
    {
        id: 'affix.suffix.minion.damage',
        kind: 'suffix',
        family: 'minion',
        applicableSlots: ['amulet', 'ring1', 'ring2', 'cloak'],
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.suffix.minion.durability',
        kind: 'suffix',
        family: 'minion',
        applicableSlots: ['amulet', 'ring1', 'ring2', 'cloak'],
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.suffix.minion.haste',
        kind: 'suffix',
        family: 'minion',
        applicableSlots: ['amulet', 'ring1', 'ring2'],
        valuesByTier: tiers(4, 20),
        valueKind: 'percent-mul',
    },
];
// family `placeable` — trap / zone / barricade buffs (2)
const PLACEABLE_AFFIXES = [
    {
        id: 'affix.suffix.placeable.damage',
        kind: 'suffix',
        family: 'placeable',
        applicableSlots: ['amulet', 'ring1', 'ring2', 'cloak', 'belt'],
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
    {
        id: 'affix.suffix.placeable.durability',
        kind: 'suffix',
        family: 'placeable',
        applicableSlots: ['amulet', 'ring1', 'ring2', 'belt'],
        valuesByTier: tiers(8, 40),
        valueKind: 'percent-mul',
    },
];
// family `support` — role buff (1)
const SUPPORT_AFFIXES = [
    {
        id: 'affix.suffix.support.healing_power',
        kind: 'suffix',
        family: 'support',
        applicableSlots: WEAPON_ACCESSORY,
        valuesByTier: tiers(6, 30),
        valueKind: 'percent-mul',
    },
];
export const SUFFIX_AFFIXES = [
    ...DEFENSE_AFFIXES,
    ...STATUS_AFFIXES,
    ...UTILITY_AFFIXES,
    ...SUSTAIN_AFFIXES,
    ...SLAYER_AFFIXES,
    ...MINION_AFFIXES,
    ...PLACEABLE_AFFIXES,
    ...SUPPORT_AFFIXES,
];
/** Full affix master pool: 35 prefix + 37 suffix = 72 affixes. */
export const AFFIX_DEFINITIONS = [
    ...PREFIX_AFFIXES,
    ...SUFFIX_AFFIXES,
];
//# sourceMappingURL=affixes.js.map